import React from "react";
import { ClipboardList, RefreshCcw } from "lucide-react";
import { AdminTopbar } from "../../layouts/AdminLayout";
import { Button } from "../../components/ui";
import { useApi } from "../../api/client";

/*
 * One row per administrative action, straight from the audit trail. Classes and
 * System Users used to live here as read-only stubs; both are now real pages of
 * their own — Classes.tsx and Users.tsx.
 */
interface AuditEntry {
  action: string;
  kind: string;
  detail: string;
  user: string;
  atLabel: string;
}

/* The colour follows what the action did, so a delete stands out from an edit. */
const actionTone: Record<string, string> = {
  created: "bg-success-soft text-success",
  imported: "bg-primary-soft text-primary",
  updated: "bg-warning-soft text-warning",
  recalculated: "bg-violet-50 text-violet-600",
  deleted: "bg-danger-soft text-danger",
  removed: "bg-danger-soft text-danger",
};

export function AdminAuditLog() {
  const { data: rows, reload } = useApi<AuditEntry[]>("/admin/audit-log", []);
  return (
    <div>
      <AdminTopbar
        title="Audit Log"
        subtitle="Every admin action is recorded — creations, updates, deletions, imports and recalculations."
        actions={<Button variant="secondary" onClick={reload}><RefreshCcw size={15} /> Refresh</Button>}
      />
      <div className="card overflow-x-auto">
        <table className="w-full min-w-[760px]">
          <thead>
            <tr className="border-b border-line bg-slate-50">
              <th className="th">Action</th>
              <th className="th">Type</th>
              <th className="th">Detail</th>
              <th className="th">User</th>
              <th className="th text-right">When</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {!rows.length && (
              <tr>
                <td colSpan={5} className="td py-8 text-center text-muted">
                  No admin activity recorded yet.
                </td>
              </tr>
            )}
            {rows.map((r, i) => (
              <tr key={i} className="transition-colors hover:bg-slate-50">
                <td className="td">
                  <span className={`rounded-md px-2.5 py-1 text-xs font-bold uppercase ${actionTone[r.action] ?? "bg-slate-100 text-slate-500"}`}>
                    {r.action}
                  </span>
                </td>
                <td className="td">
                  <span className="flex items-center gap-2 text-slate-600"><ClipboardList size={14} className="text-muted" /> {r.kind}</span>
                </td>
                <td className="td font-semibold">{r.detail}</td>
                <td className="td text-muted">{r.user}</td>
                <td className="td text-right text-muted">{r.atLabel}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
